import React, { Component } from 'react';
import gql from 'graphql-tag';
import moment from 'moment';
import { withApollo } from 'react-apollo';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { makeSelectLocation } from 'containers/App/selectors';
import ADialog from 'components/ADialog';
import LoadingIndicator from 'components/LoadingIndicator';
import SixtySeconds from 'components/SixtySeconds';
import TimeSync from 'utils/TimeSync';
import { fromIso9075DateString } from 'utils/dates';
import { USER_QUERY } from 'utils/graphql/userQueries';
import withDrawingPrizeSound from './sound';
import YouWinImg from './youwin.png';
import './DrawingPrizeAlert.css';

const LATENCY_PENALTY_MS = 1000;

//  ----------------------------------------------------------------------------------------------//
const checkEvent = (event, myId) => {
  //  it passes only winner/claimed events for the current user
  if (
    !event.user ||
    event.user.userId !== myId ||
    (event.event !== 'winner' && event.event !== 'claimed')
  ) {
    return false;
  }

  event.date = fromIso9075DateString(event.date);
  if (
    event.event === 'winner' &&
    Date.now() - TimeSync.toLocalTime(event.date.getTime()) >
      60000 - LATENCY_PENALTY_MS
  ) {
    return false;
  }

  event.totalPlaces = event.totalPlaces || 0;
  event.rank = event.rank || 0;
  return true;
};
//  ----------------------------------------------------------------------------------------------//

class DrawingPrizeAlert extends Component {
  constructor(props) {
    super(props);

    this.state = {
      user: false,
      event: false,
      error: false,
      claimed: false,
      request: false
    };

    this.handleClaim = this.handleClaim.bind(this);
    this.handleDismiss = this.handleDismiss.bind(this);
    this.handleClose = this.handleClose.bind(this);
  }

  //  ---------------------------------

  async componentDidMount() {
    await this.queryUserData();
  }

  componentWillUnmount() {
    this.unsubscibeToDrawingRoundWinners();
  }

  async queryUserData() {
    try {
      let resp = await this.props.client.query({
        query: USER_QUERY,
        name: 'userData'
      });
      this.setState({ user: resp.data.user });
      this.queryLastDrawingEvents();
      this.subscribeToDrawingRoundWinners();
    } catch (e) {
      console.error('queryUserData exception', e);
    }
  }

  async queryLastDrawingEvents() {
    const { user } = this.state;
    try {
      let resp = await this.props.client.query({
        query: QUERY_DRAWING_EVENTS,
        name: 'drawingEvents'
      });
      // resp.data:
      //   drawingsEvents:
      //     drawingsEvents: Array(30)
      //       0:
      //         event: "addTickets"
      //         user: {userId: "3", displayName: "gator", __typename: "User"}
      //         date: "2019-11-02 16:48:33.874"
      //         desc: "Some dummy drawing with even longer..special($51)!"
      //         drawingId: "1652"
      //         name: "Crash Test Dummies (Longer Drawing Name)"
      //         rank: null
      //         totalPlaces: "2"

      let events = (
        (resp &&
          resp.data &&
          resp.data.drawingsEvents &&
          resp.data.drawingsEvents.drawingsEvents) ||
        []
      ).filter(e => checkEvent(e, user.userId));

      //  clean events in rare case when player win, claims a prize
      //  within few seconds and immediately reload page or load it
      //  into other tab/browser
      events.forEach((e, idx, events) => {
        if (e.event !== 'claimed') {
          return;
        }
        const winner = events.find(
          w =>
            w.event === 'winner' &&
            w.drawingId === e.drawingId &&
            w.rank === e.rank
        );
        if (winner) {
          winner.claimed = true;
        }
      });
      events = events.filter(e => !e.claimed && e.event !== 'claimed');

      if (events.length) {
        this.setState({ event: events[0], claimed: false });
      }
    } catch (e) {
      console.error('queryLastDrawingEvents exception', e);
      this.setState({ event: false, claimed: false });
    }
  }

  subscribeToDrawingRoundWinners = () => {
    this.unsubscibeToDrawingRoundWinners();

    this.drawingRoundWinnersListener = this.props.client
      .subscribe({
        query: SUBSCRIPTION_DRAWING_WINNER,
        name: 'drawingWinner'
      })
      .subscribe({
        next: data => {
          if (!data?.data) {
            return null;
          }

          const winnerData = data.data;

          const page = {
            message: 'Winner Popup opens',
            drawingId: winnerData.drawingId,
            event: winnerData.event,
            rank: winnerData.rank,
            name: winnerData.name,
            popupOpenTime: winnerData.date
          };

          if (winnerData.autoclaim) {
            this.setState({ event: winnerData, claimed: true }, () => {
              this.props.playPrizeSound();
              this.ackDrawingWinner(winnerData.drawingId, winnerData.rank);
              this.submitUserActivity(page);
            });
          } else {
            this.setState({ event: winnerData, claimed: false }, () => {
              this.props.playPrizeSound();
              this.ackDrawingWinner(winnerData.drawingId, winnerData.rank);
              this.submitUserActivity(page);
            });
          }
        },

        error: err => {
          console.log('ROUND WINNER error:', err);
        }
      });
  };

  unsubscibeToDrawingRoundWinners = () => {
    if (this.drawingRoundWinnersListener) {
      this.drawingRoundWinnersListener.unsubscribe();
      this.drawingRoundWinnersListener = null;
    }
  };

  handleDismiss() {
    const { event } = this.state;
    const page = {
      message: 'Popup Closed after Claiming prize',
      drawingId: event.drawingId,
      event: event.event,
      rank: event.rank,
      name: event.name,
      popupCloseTime: event.date
    };
    this.submitUserActivity(page);
    this.setState({ event: false, claimed: false });
  }

  handleClose() {
    const { event } = this.state;
    const page = {
      message: 'Popup Closed after 60 seconds without claim',
      drawingId: event.drawingId,
      event: event.event,
      rank: event.rank,
      name: event.name,
      popupCloseTime: event.date
    };
    this.submitUserActivity(page);
    this.setState({ event: false, claimed: false });
  }

  async handleClaim() {
    const drawingId = parseFloat(this.state.event.drawingId);
    this.setState({ request: true, claimed: true });

    try {
      let resp = await this.props.client.mutate({
        mutation: MUTATION_CLAIM_PRIZE,
        variables: { input: { drawingId } }
      });
      // resp:
      //   data:
      //     claimPrize:
      //       message: "Ticket was claimed by player! No tickets remain"
      //       success: true

      if (resp && resp.data) {
        if (!resp.data.claimPrize.success) {
          const { event } = this.state;
          const page = {
            message: resp.data.claimPrize.message,
            drawingId: event.drawingId,
            event: event.event,
            rank: event.rank,
            name: event.name
          };
          this.submitUserActivity(page);
          console.error('claimPrize error', resp.data.claimPrize.message);
          this.setState({
            event: false,
            claimed: false,
            error: 'Failed to accept your prize.'
          });
        }
      }
    } catch (e) {
      console.error('claimPrize exception', e);
      const { event } = this.state;
      const page = {
        message: 'Failed to accept your prize.',
        drawingId: event.drawingId,
        event: event.event,
        rank: event.rank,
        name: event.name
      };
      this.submitUserActivity(page);
      this.setState({
        event: false,
        claimed: false,
        error: 'Failed to accept your prize.'
      });
    }

    this.setState({ request: false });
  }

  ackDrawingWinner(drawingId, rank) {
    return this.props.client.mutate({
      mutation: MUTATION_ACK_DRAWING_WINNER,
      variables: {
        input: {
          drawingId,
          prizeId: 0,
          place: rank,
          timestamp: Date.now()
        }
      }
    });
  }

  submitUserActivity(data) {
    const pathname = this.props.location.pathname;
    const localTime = moment().toISOString(true);
    const userAgent = navigator.userAgent;
    const pageData = {
      pathname: pathname,
      ...data
    };
    return this.props.client.mutate({
      mutation: SUBMIT_USER_ACTIVITY_PAGE_NAVIGATION,
      variables: {
        input: {
          page: JSON.stringify(pageData),
          localTime: localTime,
          userAgent
        }
      }
    });
  }

  //  ---------------------------------
  //  rendering

  renderAlert() {
    const { event, claimed, user } = this.state;
    const prizes = event.prizes && event.prizes.prizes;

    return (
      event && (
        <ADialog
          show={true}
          onClose={this.handleDismiss}
          className="a-dialog-drawings-confirm prize-alert"
          hideHeader={true}
          hideFooter={true}
          closeIcon={false}
          backdrop={'static'}
        >
          <div className="left-col">
            <div className="winner-wrapper">
              <div className="winner">
                <div className="name notranslate">{user.displayName}</div>
              </div>
              {claimed ? (
                <div className="count-down">
                  Prize successfully claimed! This prize will be added to your
                  account.
                </div>
              ) : (
                <div className="count-down">
                  <div className="count-down-text">
                    <span>You have </span>
                    <SixtySeconds onFinish={this.handleClose} />
                    <br />
                    <span>to claim this prize</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="right-col">
            <span>You&apos;ve been selected the winner for:</span>
            <div className="drawing-prize">
              Winner {event.rank} of {event.totalPlaces || 'X'}
            </div>
            <div className="drawing-name">{event.name}</div>
            {prizes && (
              <div className="prizes-wrapper">
                {prizes.map((p, idx) => (
                  <div className="prize" key={'prize-' + p.id}>
                    {p.name}
                  </div>
                ))}
              </div>
            )}
            {claimed ? (
              <span>
                <img src={YouWinImg} alt="You Win!" />
                <span>Thanks for playing!</span>
                <br />
                <button
                  className="btn btn-danger btn-close"
                  onClick={this.handleDismiss}
                  type="button"
                >
                  CLOSE
                </button>
              </span>
            ) : (
              <span>
                <span>Please click below to claim your prize</span>
                <br />
                <span>before it expires!</span>
                <br />
                <button
                  className="btn button-color-green btn-claim"
                  onClick={this.handleClaim}
                  type="button"
                  {...{ disabled: this.state.request }}
                >
                  CLAIM MY PRIZE
                </button>
              </span>
            )}
          </div>

          <div className="confetti" />
          <div className="offset-confetti" />
          {this.state.request && <LoadingIndicator overlay />}
        </ADialog>
      )
    );
  }

  renderError() {
    return (
      <ADialog
        show={!!this.state.error}
        onClose={() => this.setState({ error: false })}
        className="a-dialog-drawings-confirm error"
        hideHeader={true}
        hideFooter={true}
        closeIcon={true}
      >
        <h4>Error!</h4>
        <span>{this.state.error}</span>
      </ADialog>
    );
  }

  render() {
    return (
      <React.Fragment>
        {this.renderAlert()}
        {this.renderError()}
      </React.Fragment>
    );
  }
}

//  ---------------------------------

const QUERY_DRAWING_EVENTS = gql`
  query drawingsEvents {
    drawingsEvents {
      drawingsEvents {
        drawingId
        desc
        date
        name
        event
        user {
          userId
          displayName
        }
        rank
        totalPlaces
        prizes {
          prizes {
            id
            name
          }
        }
      }
    }
  }
`;

const SUBSCRIPTION_DRAWING_WINNER = gql`
  subscription DrawingWinner {
    drawingWinner {
      drawingId
      name
      desc
      roundNum
      wintime
      rank
      totalPlaces
      autoclaim
    }
  }
`;

const MUTATION_CLAIM_PRIZE = gql`
  mutation claimPrize($input: ClaimPrizeInput!) {
    claimPrize(input: $input) {
      success
      message
    }
  }
`;

const MUTATION_ACK_DRAWING_WINNER = gql`
  mutation ackDrawingWinner($input: AckDrawingWinner!) {
    ackDrawingWinner(input: $input)
  }
`;

const SUBMIT_USER_ACTIVITY_PAGE_NAVIGATION = gql`
  mutation MyUserActivityPageNavigation(
    $input: UserActivityPageNavigationInput!
  ) {
    userActivityPageNavigation(input: $input)
  }
`;

const mapStateToProps = createStructuredSelector({
  location: makeSelectLocation()
});

const withConnect = connect(mapStateToProps);
//  ----------------------------------------------------------------------------------------------//
export default withConnect(
  withApollo(withDrawingPrizeSound(DrawingPrizeAlert))
);
